import { supabase } from "../lib/supabase";
import { normalizeChildren, overlaySharedList } from "../lib/familyChildren";

const API_BASE = process.env.REACT_APP_API_BASE || "";

const HOUSEHOLD_LOCATION_SELECT =
  "children, postal_code, quartier, latitude, longitude, holiday_jurisdiction, holiday_jurisdiction_source";

function mapLocation(row) {
  return {
    postalCode: row?.postal_code || null,
    quartier: row?.quartier || null,
    latitude: typeof row?.latitude === "number" ? row.latitude : null,
    longitude: typeof row?.longitude === "number" ? row.longitude : null,
    holidayJurisdiction: row?.holiday_jurisdiction || null,
    holidayJurisdictionSource: row?.holiday_jurisdiction_source || null,
  };
}

function overlayLocation(household, profile) {
  const hasShared =
    Boolean(household?.postal_code) || typeof household?.latitude === "number";
  return mapLocation(hasShared ? household : profile);
}

async function loadHouseholdShare() {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user?.id) return null;
  const { data: member, error: memberError } = await supabase
    .from("household_members")
    .select("household_id")
    .eq("user_id", user.id)
    .maybeSingle();
  if (memberError) throw memberError;
  if (!member?.household_id) return null;
  const { data: household, error: householdError } = await supabase
    .from("households")
    .select(HOUSEHOLD_LOCATION_SELECT)
    .eq("id", member.household_id)
    .maybeSingle();
  if (householdError) throw householdError;
  return household || null;
}

/**
 * Current user's family profile, or null when none/empty (RLS: own row only).
 * Kids and location come from the household row when linked and populated.
 */
export async function fetchFamilyProfile() {
  const { data, error } = await supabase
    .from("family_profile")
    .select(
      "children, postal_code, quartier, latitude, longitude, completed_at, holiday_jurisdiction, holiday_jurisdiction_source",
    )
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  let children = Array.isArray(data.children) ? data.children : [];
  let location = mapLocation(data);
  try {
    const household = await loadHouseholdShare();
    if (household) {
      children = overlaySharedList(household.children, children);
      location = overlayLocation(household, data);
    }
  } catch {
    /* keep mirrored profile */
  }
  return {
    children,
    ...location,
    completedAt: data.completed_at || null,
  };
}

async function persistFamilyProfile(body) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("Bitte melde dich an.");
  const res = await fetch(`${API_BASE}/api/family-profile`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Speichern fehlgeschlagen (${res.status}).`);
  }
  return data;
}

/**
 * Persist kids + location via service role and mark the capture flow complete.
 */
export async function saveFamilyProfile({
  children,
  postalCode = null,
  quartier = null,
  latitude = null,
  longitude = null,
  holidayJurisdiction = null,
  holidayJurisdictionSource = null,
}) {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();
  if (userError || !user) {
    throw new Error("Bitte melde dich an.");
  }

  await persistFamilyProfile({
    children: normalizeChildren(children),
    postalCode,
    quartier,
    latitude,
    longitude,
    holidayJurisdiction,
    holidayJurisdictionSource,
    completed: true,
  });
}

/**
 * Patch children while keeping location / holiday fields on the server.
 * @param {unknown} children
 */
export async function saveFamilyProfileChildren(children) {
  await persistFamilyProfile({
    children: normalizeChildren(children),
  });
}
